import React, { useEffect, useLayoutEffect, useState } from "react";
import { motion } from "../../lib/motionSafe"; // polku suhteessa komponenttiin
import { createPortal } from "react-dom";
// ✅ Oletuslogo vain spinneriä varten – dashboardiin ei renderöidä logoa
import brandMarkDefault from "../../assets/logo-buukkeri-dark.webp";

/* ============================================================================
 *  EQ-palkit (pieni “ladataan” -animaatio tekstin vieressä)
 * ==========================================================================*/
function EqBar({ delay = 0, duration = 1.1, maxH = 18 }) {
  return (
    <motion.span
      className="w-[3px] rounded-sm 
                 bg-gradient-to-b from-indigo-500 to-sky-400 
                 dark:from-primary dark:to-fuchsia-400"
      initial={{ height: 4 }}
      animate={{ height: [6, maxH, 9, Math.max(10, maxH - 4), 6] }}
      transition={{ duration, repeat: Infinity, ease: "easeInOut", delay }}
    />
  );
}

function EqSpinner({ size = 28, className = "", speed = 1.1, bars = 4 }) {
  const h = typeof size === "number" ? size : parseInt(size, 10) || 28;
  const barWidth = 3;
  const gap = 3;
  const w = bars * barWidth + (bars - 1) * gap;

  return (
    <div
      className={`inline-flex items-end justify-center rounded-md px-2 py-1
                  bg-white/70 border border-black/10 text-black
                  dark:bg-black/60 dark:border-white/10 dark:text-white
                  shadow-sm ${className}`}
      role="img"
      aria-label="Ladataan"
      style={{ height: h, minWidth: w + 16 }}
    >
      <div className="flex items-end" style={{ gap }}>
        {Array.from({ length: bars }).map((_, i) => (
          <EqBar
            key={i}
            delay={i * 0.15}
            duration={speed}
            maxH={Math.max(12, h - 10)}
          />
        ))}
      </div>
    </div>
  );
}

/* (Talteen) Discopallo-variantti */
function DiscoSpinner({ size = 32, className = "", speed = 1.2 }) {
  const s = typeof size === "number" ? `${size}px` : size;
  return (
    <div
      className={`relative inline-block rounded-full overflow-hidden 
                  ring-2 ring-black/10 dark:ring-white/20 
                  shadow-[0_0_20px_rgba(0,184,255,0.25)] dark:shadow-[0_0_24px_rgba(49,255,217,0.35)] ${className}`}
      style={{ width: s, height: s }}
      role="img"
      aria-label="Ladataan"
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(140% 120% at 30% 25%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.35) 18%, rgba(99,102,241,0.35) 44%, rgba(59,130,246,0.30) 70%, rgba(0,0,0,0.25) 100%)",
        }}
      />
      <div
        className="absolute inset-0 rounded-full mix-blend-overlay opacity-70"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.35) 0 2px, transparent 2px 10px), repeating-linear-gradient(90deg, rgba(255,255,255,0.35) 0 2px, transparent 2px 10px)",
          filter: "drop-shadow(0 0 6px rgba(255,255,255,0.2))",
        }}
      />
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "conic-gradient(from 210deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.65) 40deg, rgba(255,255,255,0) 80deg)",
          mixBlendMode: "screen",
        }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: speed, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
}

/* ============================================================================
 *  Pyörivä logo + tehosteet (glow, sweep, orbit, pulse)
 * ==========================================================================*/
function SpinningLogo({
  src,
  size = 96,
  spinSec = 5,
  glow = true,
  sweep = true,
  orbit = true,
  pulse = true,
  className = "",
}) {
  const s = typeof size === "number" ? `${size}px` : size;

  return (
    <motion.div
      className={`relative grid place-items-center select-none ${className}`}
      style={{ width: s, height: s }}
      animate={pulse ? { scale: [1, 1.025, 1] } : undefined}
      transition={pulse ? { duration: 3.5, repeat: Infinity, ease: "easeInOut" } : undefined}
    >
      {/* Pyörivä logo */}
      <motion.img
        src={src}
        alt=""
        draggable="false"
        className="block w-full h-full object-contain rounded-[14%]"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: spinSec, repeat: Infinity, ease: "linear" }}
      />

      {/* Heijastusrengas logon päällä */}
      {sweep && (
        <motion.div
          className="pointer-events-none absolute inset-0 rounded-[14%] mix-blend-screen"
          style={{
            background:
              "conic-gradient(from 220deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.65) 35deg, rgba(255,255,255,0) 75deg)",
          }}
          animate={{ rotate: [0, 360] }}
          transition={{ duration: spinSec * 0.6, repeat: Infinity, ease: "linear" }}
        />
      )}

      {/* Orbit-rengas */}
      {orbit && (
        <motion.div
          className="pointer-events-none absolute rounded-full"
          style={{
            inset: "-8px",
            background:
              "conic-gradient(from 0deg, rgba(49,255,217,0.6), rgba(122,0,255,0.5), rgba(49,255,217,0.6))",
            WebkitMask:
              "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0)",
            mask:
              "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 0)",
            filter: "blur(0.3px)",
          }}
          animate={{ rotate: [0, -360] }}
          transition={{ duration: spinSec * 0.5, repeat: Infinity, ease: "linear" }}
        />
      )}

      {/* Neon-hehku */}
      {glow && (
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            boxShadow:
              "0 0 26px rgba(49,255,217,0.35), 0 0 54px rgba(122,0,255,0.25)",
          }}
          aria-hidden
        />
      )}
    </motion.div>
  );
}

/*  Pieni spinneri – default export  */
export default function BileSpinner({
  variant = "eq",
  size = 42,
  className = "",
  speed = 2.0,
  bars = 10,
}) {
  if (variant === "disco") {
    return <DiscoSpinner size={size} className={className} speed={speed} />;
  }
  return <EqSpinner size={size} className={className} speed={speed} bars={bars} />;
}

/* ============================================================================
 *  KOKO-SIVUN OVERLAY
 * ==========================================================================*/
const OVERLAY_FLAG = "__BBX_OVERLAY_OPEN__";

/** Julkinen API-komponentti (huolehtii yksittäisyyslipusta) */
export function PageOverlayLoading(props) {
  const [canRender, setCanRender] = useState(false);

  // Salli vain yksi overlay kerrallaan – tämä hook on aina samalla rivillä (ei ehdollinen)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window;
    if (w[OVERLAY_FLAG]) return; // toinen instanssi ei renderöidy
    w[OVERLAY_FLAG] = true;
    setCanRender(true);
    return () => {
      try { delete w[OVERLAY_FLAG]; } catch { w[OVERLAY_FLAG] = false; }
    };
  }, []);

  if (!canRender) return null;
  return <OverlayPortal {...props} />;
}

/** Varsinainen portaalisisältö (scroll-lukko, fade, taustat, logo, kortti) */
function OverlayPortal({
  // label = "Ladataan…",

  // 🖼 Taustakuva-asetukset – määritä siellä missä kutsut:
  // <PageOverlayLoading bgImage={hero} bgLqip={heroTiny} />
  bgImage,
  bgLqip,
  blur = 10,
  eager = true,

  overlay = true,

  // Fade-ajat (ms)
  fadeInMs = 380,
  fadeOutMs = 260,

  // Z-index ja scroll-lukko
  lockScroll = true,
  z = 2147483647,

  // Iso vesileima taustalle (valinnainen)
  bgLogo,
  bgLogoSize,
  bgLogoOpacity = 0.14,

  // Pyörivä logo kortissa
  logo,             // yleislogo (fallback)
  logoLight,        // light-tilaa varten (valinnainen)
  logoDark,         // dark-tilaa varten (valinnainen)
  markSize = 72,
  logoSpinSec = 3,
  logoGlow = false,
  logoSweep = false,
  logoOrbit = false,
  logoPulse = false,
}) {
  const [bgLoaded, setBgLoaded] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === "undefined") return false;
    const htmlHasDark = document.documentElement.classList.contains("dark");
    if (htmlHasDark) return true;
    // fallback: OS-tason teema
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });

  // 🔑 Oikea järjestys:
  // 1) Overlay mounttaa → poista bbx-booting (root ei enää piilossa boot-syystä)
  // 2) Lisää bbx-overlay-open (root PIILOSSA niin kauan kuin overlay on auki)
  // 3) Unmount → poista bbx-overlay-open → root feidaa sisään
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.remove("bbx-booting");
    html.classList.add("bbx-overlay-open");
    return () => {
      html.classList.remove("bbx-overlay-open");
    };
  }, []);

  // 🔁 Päivitä isDark kun <html> saa/menettää .dark tai kun OS-teema vaihtuu
  useEffect(() => {
    if (typeof document === "undefined") return;

    const html = document.documentElement;
    const update = () => setIsDark(html.classList.contains("dark") ||
      (window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false));

    // 1) Tarkkaillaan classlist- muutosta
    const mo = new MutationObserver(update);
    mo.observe(html, { attributes: true, attributeFilter: ["class"] });

    // 2) Kuunnellaan OS-tason muutosta
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const mqHandler = () => update();
    try {
      mq?.addEventListener?.("change", mqHandler);
    } catch {
      // Safari vanha API
      mq?.addListener?.(mqHandler);
    }

    // Initial sync
    update();

    return () => {
      mo.disconnect();
      try {
        mq?.removeEventListener?.("change", mqHandler);
      } catch {
        mq?.removeListener?.(mqHandler);
      }
    };
  }, []);

  // 🔒 Estä taustan rullaus
  useEffect(() => {
    if (!lockScroll) return;
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    const prevPad = document.body.style.paddingRight;
    const sw = window.innerWidth - html.clientWidth;
    html.style.overflow = "hidden";
    if (sw > 0) document.body.style.paddingRight = `${sw}px`;
    return () => {
      html.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPad;
    };
  }, [lockScroll]);

  const bgLogoCssWidth =
    bgLogoSize != null
      ? typeof bgLogoSize === "number"
        ? `${bgLogoSize}px`
        : String(bgLogoSize)
      : "clamp(140px, 24vmin, 280px)";

  // LQIP→BG crossfade synkataan sisääntulofadeen
  const bgFadeMs = Math.max(400, fadeInMs);

  // Valitse logo teeman mukaan (logoDark/logoLight → fallback brandMarkDefault → props.logo)
  const logoSrc = isDark
    ? (logoDark || logo || brandMarkDefault)
    : (logoLight || logo || brandMarkDefault);

  const content = (
    <motion.div
      className="fixed inset-0 grid place-items-center"
      aria-live="polite"
      aria-busy="true"
      style={{ zIndex: z }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: fadeOutMs / 1000, ease: "easeIn" } }}
      transition={{ duration: fadeInMs / 1000, ease: "easeOut" }}
    >
      {/* Taustan perusfilleri – light & dark erikseen */}
      {/* Light backlayer */}
      <div
        className="absolute inset-0 dark:hidden"
        aria-hidden
        style={{
          background:
            "radial-gradient(1200px 600px at 20% 10%, rgba(59,130,246,0.08), transparent 60%), radial-gradient(1200px 600px at 80% 10%, rgba(99,102,241,0.08), transparent 60%), #f6f7fb",
        }}
      />
      {/* Dark backlayer */}
      <div
        className="absolute inset-0 hidden dark:block"
        aria-hidden
        style={{
          background:
            "radial-gradient(1200px 600px at 20% 10%, rgba(67,56,202,0.25), transparent 60%), radial-gradient(1200px 600px at 80% 10%, rgba(14,165,233,0.18), transparent 60%), #0a0b0f",
        }}
      />

      {/* LQIP-kuva */}
      {bgLqip && (
        <img
          src={bgLqip}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 w-full h-full object-cover"
          style={{
            filter: `blur(${blur + 2}px)`,
            transform: "scale(1.06)",
            opacity: bgLoaded ? 0 : 1,
            transition: `opacity ${bgFadeMs}ms ease-out`,
          }}
          loading="eager"
          decoding="async"
          fetchpriority="high"
        />
      )}

      {/* Varsinainen taustakuva */}
      {!!bgImage && (
        <img
          src={bgImage}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 w-full h-full object-cover"
          style={{
            filter: `blur(${blur}px)`,
            transform: "scale(1.05)",
            opacity: bgLoaded ? 1 : (bgLqip ? 0 : 1),
            transition: `opacity ${bgFadeMs}ms ease-out`,
          }}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={eager ? "high" : "auto"}
          onLoad={() => setBgLoaded(true)}
        />
      )}

      {/* Overlay-sävy (light/dark) */}
      {overlay && (
        <>
          {/* Light tint */}
          <div
            className="pointer-events-none absolute inset-0 dark:hidden"
            aria-hidden
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.66) 40%, rgba(255,255,255,0.60) 100%)",
            }}
          />
          {/* Dark tint */}
          <div
            className="pointer-events-none absolute inset-0 hidden dark:block"
            aria-hidden
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.65) 40%, rgba(0,0,0,0.6) 100%)",
            }}
          />
        </>
      )}

      {/* Iso vesileima taustalle (valinnainen) */}
      {!!bgLogo && (
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ width: bgLogoCssWidth }}
          aria-hidden
        >
          <motion.img
            src={bgLogo}
            alt=""
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              opacity: bgLogoOpacity,
              mixBlendMode: isDark ? "screen" : "multiply",
            }}
            initial={{ scale: 0.985 }}
            animate={{ scale: [0.985, 1.0, 0.985] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      )}

      {/* Keskikortti – sopeutuu teeman mukaan */}
      <motion.div
        className="relative flex flex-col items-center rounded-xl px-5 py-5
                   bg-white/75 text-black ring-1 ring-black/10
                   dark:bg-black/70 dark:text-white dark:ring-white/15
                   backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,0.15)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.35)]"
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 8, opacity: 0 }}
        transition={{ duration: Math.min(0.6, fadeInMs / 1000), ease: "easeOut" }}
      >
        <SpinningLogo
          src={logoSrc}
          size={markSize}
          spinSec={logoSpinSec}
          glow={logoGlow}
          sweep={logoSweep}
          orbit={logoOrbit}
          pulse={logoPulse}
          className="mb-2"
        />

        <span className="mt-2 font-customFont2 text-xl sm:text-2xl md:text-2xl lg:text-3xl leading-tight font-light">
          Ladataan..
        </span>
      </motion.div>
    </motion.div>
  );

  return createPortal(content, document.body);
}
